import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/admin";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const EMPTY = { name: "", price_monthly: 0, price_yearly: 0, quote_limit: 0, features: [], is_active: true };

export default function Plans() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [featStr, setFeatStr] = useState("");

  const load = () => adminApi.get("/plans").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({}); setForm(EMPTY); setFeatStr(""); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p });
    setFeatStr((p.features || []).join("\n"));
  };
  const save = async () => {
    const payload = {
      ...form,
      price_monthly: parseFloat(form.price_monthly) || 0,
      price_yearly: parseFloat(form.price_yearly) || 0,
      quote_limit: parseInt(form.quote_limit, 10) || 0,
      features: featStr.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing?.id) await adminApi.put(`/plans/${editing.id}`, payload);
      else await adminApi.post("/plans", payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await adminApi.delete(`/plans/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="sq-label">Subscription plans</div>
          <h1 className="text-4xl font-heading font-bold mt-1 text-secondary">Plans</h1>
        </div>
        <Button onClick={openNew} data-testid="plan-new-btn"><Plus size={14} className="mr-1" /> New plan</Button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="sq-card p-5 relative" data-testid="plan-card">
            {!p.is_active && (
              <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                Inactive
              </span>
            )}
            <h3 className="font-heading text-xl font-bold">{p.name}</h3>
            <div className="flex items-baseline gap-1 mt-2">
              <span className="text-3xl font-heading font-extrabold text-primary">₹{p.price_monthly}</span>
              <span className="text-muted-foreground text-sm">/ mo</span>
            </div>
            <div className="text-xs text-muted-foreground">or ₹{p.price_yearly} / year</div>
            <div className="text-xs mt-2">
              {p.quote_limit === 0 ? "Unlimited quotes" : `${p.quote_limit} quotes / mo`}
            </div>
            <ul className="mt-3 space-y-1 text-sm">
              {(p.features || []).map((f) => (
                <li key={f} className="flex gap-2"><span className="text-primary">•</span>{f}</li>
              ))}
            </ul>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid="plan-edit-btn">
                <Pencil size={14} className="mr-1" /> Edit
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(p)} className="text-destructive">
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-muted-foreground">No plans yet.</div>}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit plan" : "New plan"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Field label="Name" span>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="h-11" data-testid="plan-name" />
            </Field>
            <Field label="Price / month (₹)">
              <Input type="number" value={form.price_monthly}
                onChange={(e) => setForm({ ...form, price_monthly: e.target.value })} className="h-11" data-testid="plan-price-monthly" />
            </Field>
            <Field label="Price / year (₹)">
              <Input type="number" value={form.price_yearly}
                onChange={(e) => setForm({ ...form, price_yearly: e.target.value })} className="h-11" data-testid="plan-price-yearly" />
            </Field>
            <Field label="Quote limit (0 = unlimited)" span>
              <Input type="number" value={form.quote_limit}
                onChange={(e) => setForm({ ...form, quote_limit: e.target.value })} className="h-11" data-testid="plan-quote-limit" />
            </Field>
            <Field label="Features (one per line)" span>
              <textarea value={featStr} onChange={(e) => setFeatStr(e.target.value)}
                rows={4} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="plan-features" />
            </Field>
            <div className="col-span-2 flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="plan-active" />
              <Label>Active (visible to factories)</Label>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} data-testid="plan-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, span, children }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <Label className="sq-label">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
